import React, { FC, ElementType, useMemo, useCallback, useRef } from "react";

import { useForceUpdate } from "../../hooks";
import { DialogContext, DialogContextValue } from "./Dialog.context";
import { DialogActionsContext } from "./DialogActions.context";
import { DialogResultType } from "./Dialog.types";

import type { DialogResult, DialogActions } from "./Dialog.types";

const DialogContextProvider = DialogContext.Provider;
const DialogActionsContextProvider = DialogActionsContext.Provider;

const DialogProvider: FC = ({ children }) => {
  const forceUpdate = useForceUpdate();

  const componentRef = useRef<ElementType | null>(null);
  const promiseResolver = useRef<((value: DialogResult<unknown> | PromiseLike<DialogResult<unknown>>) => void) | null>();
  const propsRef = useRef<Object>({});

  const dispose = useCallback(() => {
    componentRef.current = null;

    propsRef.current = {};

    promiseResolver.current = null;
  }, []);

  const openModal = useCallback(
    async function openModal<Props>(Component: ElementType<Props>, props?: Props) {
      return new Promise<DialogResult<unknown>>((resolve) => {
        componentRef.current = Component;

        propsRef.current = props ?? {};

        promiseResolver.current = resolve;

        forceUpdate();
      });
    },
    [forceUpdate]
  );

  const closeModal = useCallback(() => {
    dispose();
    forceUpdate();
  }, [dispose, forceUpdate]);

  const modalActions = useMemo<DialogActions>(
    () => ({
      proceed: (data) => {
        if (promiseResolver.current != null) {
          promiseResolver.current({
            type: DialogResultType.Proceed,
            data,
          });

          closeModal();
        }
      },
      cancel: (data) => {
        if (promiseResolver.current != null) {
          promiseResolver.current({
            type: DialogResultType.Cancel,
            data,
          });

          closeModal();
        }
      },
      dismiss: (data) => {
        if (promiseResolver.current != null) {
          promiseResolver.current({
            type: DialogResultType.Dismiss,
            data,
          });

          closeModal();
        }
      },
    }),
    [closeModal]
  );

  const value = useMemo<DialogContextValue>(
    () => ({
      openModal,
    }),
    [openModal]
  );

  // We guard against nullability below
  const Component = componentRef.current!;

  return (
    <DialogContextProvider value={value}>
      {children}
      {componentRef.current !== null && (
        <DialogActionsContextProvider value={modalActions}>
          <Component key="modal" {...propsRef.current} />
        </DialogActionsContextProvider>
      )}
    </DialogContextProvider>
  );
};

export { DialogProvider };
